import {
  type NoSerialize,
  Slot,
  component$,
  noSerialize,
  useSignal,
  useVisibleTask$,
} from "@builder.io/qwik";
import {
  routeLoader$,
  type DocumentHead,
  type RequestHandler,
} from "@builder.io/qwik-city";
import { context, defaultTextMapSetter, propagation } from "@opentelemetry/api";
import { Toaster } from "qwik-sonner";
import * as Sentry from "@sentry/browser";
import type { Clerk } from "@clerk/clerk-js";
import { useProvideClerk } from "~/hooks/use-clerk";
import { getAuthRequestState } from "~/lib/clerk";

export const useAuthState = routeLoader$(({ sharedMap }) => {
  return getAuthRequestState(sharedMap);
});

export const onGet: RequestHandler = async ({ cacheControl }) => {
  // Control caching for this request for best performance and to reduce hosting costs:
  // https://qwik.dev/docs/caching/
  cacheControl({
    public: true,
    // Always serve a cached response by default, up to a week stale
    staleWhileRevalidate: 60 * 60 * 24 * 7,
    // Max once every 5 seconds, revalidate on the server to get a fresh version of this page
    maxAge: 5,
  });
};

export const head: DocumentHead = ({ head }) => {
  const carrier: Record<string, string> = {};
  propagation.inject(context.active(), carrier, defaultTextMapSetter);

  return {
    title: head.title ? `${head.title} | DreamCoachMatch` : "DreamCoachMatch",
    meta: [
      ...Object.entries(carrier).map(([k, v]) => ({
        name: k,
        content: v,
      })),
      {
        property: "og:image",
        content: "https://form-data.dreamcoachmatch.com/public/og.jpg",
      },
    ],
  };
};

export default component$(() => {
  const clerk = useSignal<NoSerialize<Clerk | undefined>>(undefined);

  // Clerk initialization needs to be done client-side to check if user is signed in
  // eslint-disable-next-line qwik/no-use-visible-task
  useVisibleTask$(async () => {
    const { Clerk } = await import("@clerk/clerk-js");
    const newClerk = new Clerk(import.meta.env.VITE_CLERK_PUBLISHABLE_KEY);
    await newClerk.load();

    clerk.value = noSerialize(newClerk);
  });

  useProvideClerk(clerk);

  // Browser-side Opentelemetry
  // eslint-disable-next-line qwik/no-use-visible-task
  useVisibleTask$(() => {
    if (!import.meta.env.PROD) {
      return;
    }

    Sentry.init({
      dsn: "https://7eee74b9e1beef52e3dec4b648261396@o4508840544698368.ingest.de.sentry.io/4508840773550160",
      integrations: [
        Sentry.browserTracingIntegration(),
        Sentry.replayIntegration(),
      ],
      allowUrls: ["localhost", /^https:\/\/dreamcoachmatch\.com/],
      // Tracing
      tracesSampleRate: 1.0, //  Capture 100% of the transactions
      // Set 'tracePropagationTargets' to control for which URLs distributed tracing should be enabled
      tracePropagationTargets: ["localhost", /^https:\/\/dreamcoachmatch\.com/],
      // Session Replay
      replaysSessionSampleRate: 0.1, // This sets the sample rate at 10%. You may want to change it to 100% while in development and then sample at a lower rate in production.
      replaysOnErrorSampleRate: 1.0, // If you're not already sampling the entire session, change the sample rate to 100% when sampling sessions where errors occur.
    });
  });

  return (
    <>
      <div class="z-100 fixed">
        <Toaster class="fixed" richColors />
      </div>
      <Slot />
    </>
  );
});
